import classNames from 'classnames'
import moment from 'moment'
import { FC } from 'react'
import { ReactComponent as AISparkleIcon } from 'src/assets/svg/ai-sparkle-large.svg'
import { Skeleton } from 'src/components/Skeleton'
import { DeepDive } from 'src/services/aiListeningApi/brandProfile.types'
import { EngineCards } from './EngineCards'
import { OutcomeSummary } from './OutcomeSummary'
import styles from './deepDive.module.scss'

interface ReloadHistoryProps {
  checks: DeepDive['checks']
  emptyText: string
  isLoading: boolean
  onSelect: (checkedAt: string) => void
  // The run on show (its deep-dive), and which tab it is.
  run?: DeepDive
  selectedCheckedAt?: string
}

// Day first, as slash dates are read everywhere else in the app. Runs on the
// same day are told apart by their time.
const tabLabel = (checkedAt: string, checks: DeepDive['checks']) => {
  const day = moment(checkedAt).format('DD/MM/YYYY')
  const sameDay = checks.filter(
    (check) => moment(check.checkedAt).format('DD/MM/YYYY') === day
  )
  return sameDay.length > 1
    ? `${day} ${moment(checkedAt).format('HH:mm')}`
    : day
}

// Every run of the prompt in the top-bar date range, newest first: pick one
// to see its outcome, tiles and engine answers as they were.
export const ReloadHistory: FC<ReloadHistoryProps> = ({
  checks,
  emptyText,
  isLoading,
  onSelect,
  run,
  selectedCheckedAt,
}) => {
  const activeCheckedAt = selectedCheckedAt ?? checks[0]?.checkedAt
  const activeCheck = checks.find(
    (check) => check.checkedAt === activeCheckedAt
  )

  return (
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>Reload History</h2>

      {checks.length === 0 ? (
        <p className={styles.emptyText}>{emptyText}</p>
      ) : (
        <>
          <div className={styles.summaryBox}>
            <div className={styles.summaryText}>
              <span className={styles.summaryTitle}>AI Summary</span>
              <span className={styles.summarySubtitle}>
                Aggregated insights from AI tools
              </span>
              {isLoading ? (
                <Skeleton height={48} width="80%" />
              ) : (
                <p className={styles.summaryBody}>
                  {run?.outcome?.narrative ||
                    'No summary for this run — the AI models gave no usable answers.'}
                </p>
              )}
            </div>
            <AISparkleIcon className={styles.summaryArt} />
          </div>

          <div className={styles.runTabs} role="tablist">
            {checks.map((check) => (
              <button
                aria-selected={check.checkedAt === activeCheckedAt}
                className={classNames(styles.runTab, {
                  [styles.runTabActive]: check.checkedAt === activeCheckedAt,
                })}
                key={check.checkedAt}
                onClick={() => onSelect(check.checkedAt)}
                role="tab"
                type="button"
              >
                {tabLabel(check.checkedAt, checks)}
              </button>
            ))}
          </div>

          <div className={styles.runPanel}>
            {isLoading && <Skeleton height={320} radius={16} width="100%" />}
            {!isLoading &&
              activeCheck &&
              activeCheck.status !== 'completed' && (
                <p className={styles.emptyText}>
                  {activeCheck.status === 'failed'
                    ? 'This run failed: none of the AI models answered.'
                    : 'This run is still collecting AI answers.'}
                </p>
              )}
            {!isLoading &&
              run?.hasData &&
              activeCheck?.status === 'completed' && (
                <>
                  <OutcomeSummary data={run} />
                  <h3 className={styles.blockTitleLg}>
                    AI Engine Responses Comparison (Monitored Engines)
                  </h3>
                  <EngineCards engines={run.engines} />
                </>
              )}
          </div>
        </>
      )}
    </section>
  )
}
